import { tokens, Typography, Stack } from '@cast/design-system';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { useOptimizationWorkloads } from '@cast/split-me/features/workload-optimization/hooks';

import {
  getOverlapInfo,
  getPolicyRationaleFull,
  getMetricsGridItems,
  getWorkloadRows,
} from './proposedPoliciesUtils';
import { ProposedPolicyAssignmentRule } from './ProposedPolicyAssignmentRule';
import { ProposedPolicyOverlapAlert } from './ProposedPolicyOverlapAlert';
import { MetricsGrid } from './MetricsGrid';
import { ProposedPolicyWorkloadsTable } from './ProposedPolicyWorkloadsTable';
import type {
  AutopilotRecommendation,
  WrappedPolicy,
} from '../../../open-api-types/autopilot-types';

type ProposedPolicyDetailProps = {
  policy: WrappedPolicy;
  recommendation: AutopilotRecommendation;
};

export const ProposedPolicyDetail = ({ policy }: ProposedPolicyDetailProps) => {
  const { cluster } = useCluster();
  const { workloads } = useOptimizationWorkloads();
  const policyName = policy.payload.name;
  const settingsRowsFlat = getMetricsGridItems(policy);
  const settingsRows = [
    settingsRowsFlat.slice(0, 4),
    settingsRowsFlat.slice(4),
  ].filter((r) => r.length > 0);
  const overlap = getOverlapInfo(policy, workloads);
  const workloadRows = getWorkloadRows(policy);
  const totalCount = policy.targetedWorkloads?.totalCount ?? 0;
  const fullRationale = getPolicyRationaleFull(policy);

  return (
    <Stack
      gap={24}
      mt={16}
      data-testid={`proposed-policy-detail-${policyName}`}
    >
      <Stack gap={4}>
        <Typography
          variant="P12B"
          sx={{ color: tokens.theme.fg.structural.primary }}
        >
          Rationale:
        </Typography>
        <Typography
          variant="P12R"
          sx={{ color: tokens.theme.fg.structural.secondary }}
        >
          {fullRationale}
        </Typography>
      </Stack>

      {/* // todo:autopilot not included in the designs*/}
      {overlap && <ProposedPolicyOverlapAlert overlap={overlap} />}

      <MetricsGrid
        rows={settingsRows}
        testId={`policy-settings-grid-${policyName}`}
        title="Settings"
      />

      <ProposedPolicyWorkloadsTable
        policyName={policyName}
        clusterId={cluster.id}
        totalCount={totalCount}
        rows={workloadRows}
      />

      <ProposedPolicyAssignmentRule policy={policy} />
    </Stack>
  );
};
